import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import endsWith from 'lodash/endsWith';
import deepFreeze from 'deep-freeze';
import {
  UPDATE_QUANTITY,
  UPDATE_ZIP,
  UPDATE_FULFILLMENTMETHOD,
  CART_MODAL_HIDE,
  CART_SAVE_STARTED,
  CART_SAVE_SUCCESS,
  CART_SAVE_FAILED,
  APP_MOUNT,
  CART_RESET,
} from '../actions/actionTypes';

export const initialState = deepFreeze({
  quantity: 1,
  zipCode: null,
  displayModal: false,
  isLoading: false,
  cartTotal: 0.0,
  currency: 'USD',
  totalItemCount: 0,
  addedItems: [],
  errorModel: [],
  isError: false,
  fulfillmentMethod: '',
});

const hasMatchingFulfillment = (item, cartItem) => {
  const fm = cartItem.fulfillmentModel[0];

  // If BOPIS, BOSS, Special Order
  if (fm.pickUpStoreAddress) {
    const storeId = item.storeId // BOPIS/BOSS
      || item.fulfillmentLocation; // Special Order

    return endsWith(fm.pickUpStoreAddress, storeId);
  }

  // If BODFS, STH
  return true;
};

// In the case of adding items via the Check Nearby Stores modal, we won't
// necessarily have all the relevant pricing data from the `sku` api (since
// pricing can vary by store). We can get around this by taking the itemDetails
// used in the Add to Cart request, matching them with the itemModel returned
// from the Cart API, and attaching the data we want
// (price, image, name, description).
const getSummarizedItemDetails = (itemsAdded, cartItemModels) => {
  const itemsWithCorrectPricing = itemsAdded.map((item) => {
    const match = cartItemModels.find((cartItem) => (
      item.itemId.toString() === cartItem.itemId.toString()
      && hasMatchingFulfillment(item, cartItem)
    ));

    return {
      // Carry over properties from Add to Cart request.
      ...item,
      // Trying to minimize storing redundant junk data in redux.
      // We want to keep `item's` quantity since the cart API will return
      // the total quantity of that item in your cart (not just what we added).
      price: match.specialOfferPrice || match.unitPrice,
      unitPrice: match.unitPrice,
      specialOfferPrice: match.specialOfferPrice,
      name: match.brandName,
      description: match.description,
      imageSrc: match.image,
    };
  });

  // the same item might be priced differently by store
  const grouped = groupBy(itemsWithCorrectPricing, (item) => `${item.itemId} and ${item.price}`);

  return map(grouped, (group) => {
    // all properties but `quantity` should be identical here
    const baseItem = group[0];
    return {
      ...baseItem,
      quantity: sumBy(group, (item) => Number(item.quantity)),
    };
  });
};

export default (state = initialState, action) => {
  switch (action.type) {
  case APP_MOUNT: {
    return {
      ...initialState,
    };
  }

  case UPDATE_QUANTITY:
    return {
      ...state,
      quantity: action.quantity,
    };

  case UPDATE_ZIP:
    return {
      ...state,
      zipCode: action.zipCode,
    };

  case UPDATE_FULFILLMENTMETHOD:
    return {
      ...state,
      fulfillmentMethod: action.fulfillmentMethod,
    };

  case CART_MODAL_HIDE:
    return {
      ...state,
      displayModal: false,
    };

  case CART_SAVE_STARTED:
    return {
      ...state,
      displayModal: true,
      isError: false,
      isLoading: true,
    };

  case CART_SAVE_SUCCESS: {
    const { cartData, itemDetails } = action;
    return {
      ...state,
      isLoading: false,
      cartTotal: cartData.cartTotal,
      currency: cartData.currency,
      totalItemCount: cartData.totalItemCount,
      addedItems: getSummarizedItemDetails(itemDetails, cartData.itemModels),
      errorModel: [],
    };
  }

  case CART_SAVE_FAILED:
    if (!Array.isArray(action.cartError)) {
      return {
        ...state,
        isLoading: false,
        isError: true,
        addedItems: [],
        errorModel: [],
      };
    }
    return {
      ...state,
      isLoading: false,
      addedItems: [],
      isError: true,
      errorModel: action.cartError.map((item) => ({
        errorCode: item.errorCode,
        description: item.description,
      })),
    };

  case CART_RESET:
    return {
      ...state,
      errorModel: [],
    };

  default:
    return state;
  }
};
